import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, UserPlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageTitle } from "@/components/app/ui";
import { api, errMsg } from "@/lib/api";

const ROLES = [["editor", "Editor — create & edit content"], ["approver", "Approver — approve, publish & edit"], ["viewer", "Viewer — read only"]];

export default function Team() {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ name: "", email: "", role: "editor" });
  const load = () => api.get("/app/team").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!d) return <div className="h-64 rounded-2xl bg-muted animate-pulse" />;
  const isOwner = ["owner", "admin"].includes(d.my_role);
  return (
    <div data-testid="team-page">
      <PageTitle title="Team" copy={`Invite teammates with the right level of access. ${d.members.length}/${d.limit} seats used on your plan.`} />
      {isOwner && (
        <form onSubmit={(e) => { e.preventDefault(); act(() => api.post("/app/team/invite", f), "Invitation sent").then(() => setF({ name: "", email: "", role: "editor" })); }} className="card-box p-6 grid md:grid-cols-4 gap-4 items-end">
          <div className="space-y-2"><Label>Name</Label><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="team-name-input" /></div>
          <div className="space-y-2"><Label>Email</Label><Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="team-email-input" /></div>
          <div className="space-y-2"><Label>Role</Label><Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger data-testid="team-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>{ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
          <button className="btn-brand" data-testid="team-invite-btn"><UserPlus className="h-4 w-4" /> Send invite</button>
        </form>
      )}
      <div className="mt-6 card-box overflow-x-auto">
        <Table data-testid="team-table">
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>{d.members.map((m) => (
            <TableRow key={m.id} data-testid={`team-row-${m.email}`}>
              <TableCell className="font-medium">{m.name}</TableCell><TableCell className="text-xs">{m.email}</TableCell>
              <TableCell>{isOwner && ["editor", "approver", "viewer"].includes(m.role) ? (
                <Select value={m.role} onValueChange={(v) => act(() => api.patch(`/app/team/${m.id}`, { role: v }), "Role updated")}><SelectTrigger className="w-32 h-9" data-testid={`team-role-${m.email}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map(([v]) => <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>)}</SelectContent></Select>
              ) : <span className="capitalize">{m.role}</span>}</TableCell>
              <TableCell className="text-xs">{m.email_verified ? "Active" : "Invited"}</TableCell>
              <TableCell>{isOwner && ["editor", "approver", "viewer"].includes(m.role) && <button onClick={() => act(() => api.delete(`/app/team/${m.id}`), "Removed")} className="text-muted-foreground hover:text-red-500" data-testid={`team-remove-${m.email}`}><Trash2 className="h-4 w-4" /></button>}</TableCell>
            </TableRow>))}</TableBody>
        </Table>
      </div>
    </div>
  );
}
